"use client";

import { useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import price from "@/utils/price";
import { useQuery } from "@tanstack/react-query";
import { useRouter, usePathname } from "next/navigation";
import { Trans } from "@lingui/macro";
import { useDebounce } from "@/hooks/use-debounce";
import { sendGAEventTrigger } from "@/utils/analytics-trigger";
import ImageWithFallback from "./image-with-fallback";

const fetchSearch = async (searchTerm: string, lang: string) => {
  const res = await fetch(`/api/search?q=${encodeURIComponent(searchTerm)}&lang=${lang}&limit=10`);

  if (!res.ok) {
    throw new Error("Error fetching search results");
  }

  return res.json();
};

interface SearchResultsProps {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  lang: string;
  onClose: () => void;
}

const SearchModal: React.FC<SearchResultsProps> = ({
  searchTerm,
  onClose,
  lang,
  setSearchTerm,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const modalRef = useRef<HTMLDivElement | null>(null);

  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      onClose();
    }
    previousPathnameRef.current = pathname;
  }, [pathname, onClose]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  useEffect(() => {
    const wasSearched = localStorage.getItem("search-term");
    if (searchTerm.length >= 3 && (!wasSearched || wasSearched !== searchTerm)) {
      localStorage.setItem("search-term", searchTerm);
      sendGAEventTrigger("search_term", {
        send_to: `${process.env.NEXT_PUBLIC_GTM_MEASUREMENT_ID}`,
        value: searchTerm,
        category: "product_view",
        label: "Search term",
      });
    }
  }, [searchTerm]);
  const { data, isFetching } = useQuery({
    queryKey: ["search", debouncedSearchTerm],
    queryFn: () => fetchSearch(debouncedSearchTerm, lang),
    enabled: debouncedSearchTerm.length >= 3,
    staleTime: 1000 * 60 * 5,
  });

  const searchResults = useMemo(() => {
    if (data?.success) {
      return data?.data?.items || [];
    }
    return [];
  }, [data]);

  const handleSearch = (itemId: string) => {
    router.push(`/product/${itemId}`);
    if (pathname.includes("/product/") && pathname.includes(itemId)) {
      onClose();
      setSearchTerm("");
    }
  };

  function removeHtmlTags(str: string) {
    return unescapeHtml(str).replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, " ");
  }
  function unescapeHtml(safe: string) {
    return safe
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'");
  }
  return (
    <main className="h-full w-full bg-white px-[15px] md:w-[275px] lg:w-[380px]" ref={modalRef}>
      {isFetching && (
        <div className="flex min-h-[200px] items-center justify-center">
          <Image src="/loader.gif" width="30" height="30" alt="Loader" loading="lazy" />
        </div>
      )}
      {!isFetching && searchResults.length === 0 && (
        <p className="py-[15px]">
          <Trans>No products available</Trans>
        </p>
      )}

      {!isFetching &&
        searchResults.length > 0 &&
        searchResults.map((item: any) => (
          <div
            key={item.id}
            className="flex cursor-pointer py-2.5"
            onClick={() => handleSearch(item.seo_url)}
          >
            <div className="flex w-full items-center gap-3 md:items-start">
              <div className="w-1/4">
                <ImageWithFallback
                  width="100"
                  height="58"
                  src={item.image}
                  alt={item.name}
                  loading="lazy"
                />
              </div>
              <div className="w-3/4">
                <p className="line-clamp-1 text-ellipsis text-[15px] duration-300 ease-in hover:text-primary md:mb-1 lg:mb-2.5">
                  <a href={item.href}>{item.name}</a>
                </p>
                {item.meta_description && (
                  <p className="mb-2.5 hidden text-xs text-[#282828] sm:hidden lg:line-clamp-3">
                    {removeHtmlTags(item.meta_description)}
                  </p>
                )}
                {(item.price || item.price === 0) && (
                  <div className="price text-sm font-medium">
                    {!item.special ? (
                      <>{price(item.price)}</>
                    ) : (
                      <div className="flex items-baseline gap-2 md:flex-col md:gap-0 lg:flex-row lg:gap-2.5">
                        <span className="text-sm text-red-600">{price(item.special)}</span>
                        <span className="text-xs text-red-600 line-through">
                          {price(item.price)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
    </main>
  );
};

export default SearchModal;
